/**
 * Moving hazards of set pieces (phase 2) – plain data inside a SegmentDefinition, like
 * obstacles and terrain. Segment-local space (origin = CONNECTOR_IN, +z = running direction).
 *
 * A hazard is armed by one of the segment's dynamic event sockets (DynamicEventManager) and then
 * plays a deterministic timeline: idle → warning (telegraph) → active. Its gameplay colliders
 * are computed every step from that timeline (hazards/), the presenter animates the rig nodes.
 */
export const HazardKind = {
  /** Steel beam load (OBS_CRANE_LOAD_A) lowered across all lanes, swinging – slide under it. */
  CraneBeam: 'CRANE_BEAM',
  /** Concrete block load (OBS_CRANE_LOAD_B) lowered into one lane – change lanes. */
  CraneBlock: 'CRANE_BLOCK',
  /** Concrete block load (OBS_CRANE_LOAD_B) lowered over the track, swinging across all lanes – dodge it. */
  CraneSwing: 'CRANE_SWING',
  /** Excavator (VEH_EXCAVATOR_A) beside the track dumps a rubble heap into the outer lane. */
  Excavator: 'EXCAVATOR',
  /** Forklift or truck driving towards the runner in a lane, maybe changing lanes once. */
  Vehicle: 'VEHICLE',
} as const;
export type HazardKind = (typeof HazardKind)[keyof typeof HazardKind];

export const VehicleType = {
  Forklift: 'FORKLIFT',
  Truck: 'TRUCK',
  /** The big cable drum (OBS_CABLE_REEL_A × CABLE_REEL_SCALE) rolling towards the runner – maybe across a lane. */
  CableReel: 'CABLE_REEL',
} as const;
export type VehicleType = (typeof VehicleType)[keyof typeof VehicleType];

interface HazardBase {
  /** e.g. HAZ_CRANE_BEAM – unique per segment, referenced by obstacle variants. */
  readonly id: string;
  /** Dynamic event socket whose arming starts the hazard's timeline. */
  readonly socketId: string;
}

/** Tower crane (VEH_CRANE_A) beside the track whose trolley carries the load. */
export interface CraneMount {
  readonly x: number;
  readonly z: number;
}

export interface CraneBeamHazard extends HazardBase {
  readonly kind: typeof HazardKind.CraneBeam;
  /** z of the beams at rest (they span the whole track). */
  readonly z: number;
  /** Underside of the beams at the bottom of the swing – slide height. */
  readonly clearance: number;
  readonly crane: CraneMount;
}

export interface CraneBlockHazard extends HazardBase {
  readonly kind: typeof HazardKind.CraneBlock;
  readonly lane: number;
  readonly z: number;
  readonly crane: CraneMount;
}

/**
 * The load swings across all lanes like a pendulum (period, reach: HazardsConfig.craneSwing). Its swing
 * is phase-locked to the runner's arrival at `z`: an outer lane = the load reaches its turning point over
 * that lane right then; the centre lane = it sweeps through the centre lane (in direction `sweep`).
 */
export interface CraneSwingHazard extends HazardBase {
  readonly kind: typeof HazardKind.CraneSwing;
  /** Lane the load is over when the runner arrives – the one lane it blocks for the sequence validator. */
  readonly lane: number;
  /** Centre lane only: the load sweeps towards +x (1) or −x (−1) when the runner arrives. */
  readonly sweep?: 1 | -1;
  readonly z: number;
  readonly crane: CraneMount;
}

export interface ExcavatorHazard extends HazardBase {
  readonly kind: typeof HazardKind.Excavator;
  /** Slew centre of the machine beside the track (outside the fence). */
  readonly machine: { readonly x: number; readonly z: number };
  /** Outer lane it dumps into and z of the rubble heap. */
  readonly lane: number;
  readonly z: number;
}

export interface VehicleHazard extends HazardBase {
  readonly kind: typeof HazardKind.Vehicle;
  readonly vehicle: VehicleType;
  /** Lane it starts in – it faces the runner (drives towards −z). */
  readonly lane: number;
  /** Origin while it waits, engine running. */
  readonly startZ: number;
  /** Its front meets the runner here – the departure is timed for it (key line). */
  readonly meetZ: number;
  /** Origin where it stops after passing the runner. */
  readonly stopZ: number;
  /** One telegraphed lane change: starts when the origin passes `atZ`. */
  readonly laneChange?: { readonly toLane: number; readonly atZ: number };
  /**
   * Zig-zag (the rolling cable drum, user 2026-10-05: "zickzack hin und her … an der Bande abgestoßen"): from its start
   * lane it rolls diagonally `toward` −1 left / +1 right and bounces off the barriers, one crossing of the road every
   * `crossing` m of its path (zigzagPose). Not together with laneChange.
   */
  readonly zigzag?: { readonly toward: -1 | 1; readonly crossing: number };
  /** Forklift: carriage lowered or raised, with or without the wrapped pallet. */
  readonly forks?: 'LOW' | 'HIGH';
  readonly load?: boolean;
  /**
   * Height of the floor it rolls on (m) – an upper floor (the shell's slab top). Such a vehicle is no matter of the
   * ground route: no decision row, no occupied ground, no blocked bottles there (hazardRules). Default 0 = the road.
   */
  readonly elevation?: number;
}

export type HazardDefinition = CraneBeamHazard | CraneBlockHazard | CraneSwingHazard | ExcavatorHazard | VehicleHazard;
